import { useTranslator } from '@translator-app/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';

import { useKioskMode } from '../../contexts/kiosk-context';
import { useLanguageContext } from '../../contexts/language-context';
import MenuIcon from '../../icons/menu.svg';
import ShoppingCartIcon from '../../icons/shopping-cart.svg';
import CloseIcon from '../../icons/x.svg';
import locales from '../../locales.json';
import { WoytiLogo } from '../atoms/woyti-logo';

const WOYTI_CONSUMER_URL = 'https://woyti.com/consument';

interface NavItem {
  hash: string;
  labelKey: string;
}

const NAV_ITEMS: NavItem[] = [
  { hash: 'hoe-werkt-het', labelKey: 'siteNav.howItWorks' },
  { hash: 'download', labelKey: 'siteNav.download' },
  { hash: 'contact', labelKey: 'siteNav.contact' },
];

interface LanguageSwitcherProps {
  onSelect?: () => void;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ onSelect }) => {
  const router = useRouter();
  const { setLanguage } = useLanguageContext();
  const activeLocale = router.locale || router.defaultLocale || 'en';

  const switchLocale = (locale: string) => {
    if (locale === activeLocale) {
      return;
    }

    setLanguage(locale);
    router.push(router.pathname, router.asPath, { locale });
    onSelect?.();
  };

  return (
    <div className="inline-flex gap-1 rounded-full bg-gray-100 p-1">
      {locales
        .filter((locale) => locale !== 'x')
        .map((locale) => (
          <button
            key={locale}
            type="button"
            onClick={() => switchLocale(locale)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium uppercase transition md:text-sm ${
              activeLocale === locale
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {locale}
          </button>
        ))}
    </div>
  );
};

export interface SiteNavProps {
  logoHref?: string;
  cartHref?: string;
  cartCount?: number;
  showCart?: boolean;
  shopLinkActive?: boolean;
}

export const SiteNav: React.FC<SiteNavProps> = (props) => {
  const { logoHref = '/', cartHref, cartCount = 0, showCart = true, shopLinkActive = false } = props;

  const { isKioskMode } = useKioskMode();
  const { translate } = useTranslator();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  React.useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const logoContent = (
    <>
      <WoytiLogo />
      <span className="font-display-bold text-xl tracking-tight text-primary">WOYTI</span>
    </>
  );

  const handleCartClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!cartHref) {
      event.preventDefault();
      alert(translate('siteNav.cartUnavailable'));
    }
  };

  const cartButton = (
    <a
      href={cartHref ?? '#'}
      onClick={handleCartClick}
      className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-lg transition-colors hover:bg-primary-dark"
      aria-label={translate('siteNav.cart')}
    >
      <ShoppingCartIcon className="h-4 w-4" />
      <span>{cartCount}</span>
    </a>
  );

  const shopLink = (
    <NextLink
      href="/"
      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
        shopLinkActive
          ? 'bg-primary text-primary-foreground hover:bg-primary-dark'
          : 'text-muted-foreground hover:text-primary'
      }`}
      aria-current={shopLinkActive ? 'page' : undefined}
    >
      {translate('siteNav.shop')}
    </NextLink>
  );

  const navTrailingItem = shopLinkActive ? shopLink : showCart ? cartButton : null;

  if (isKioskMode) {
    if (!showCart) {
      return null;
    }
    return <div className="fixed top-4 right-4 z-50">{cartButton}</div>;
  }
  return (
    <>
      <div className="h-16">
        <nav className="fixed inset-x-0 top-0 z-50 h-16 border-b border-border bg-white/90 backdrop-filter backdrop-blur-md">
          <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-6">
            <div className="flex min-w-0 items-center gap-3">
              <NextLink href={logoHref} className="flex min-w-0 shrink items-center gap-1">
                {logoContent}
              </NextLink>
            </div>

            <div className="hidden lg:block">
              <LanguageSwitcher />
            </div>

            <ul className="hidden items-center gap-6 lg:flex">
              {NAV_ITEMS.map((item) => (
                <li key={item.hash}>
                  <a
                    href={`${WOYTI_CONSUMER_URL}/#${item.hash}`}
                    className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
                  >
                    {translate(item.labelKey)}
                  </a>
                </li>
              ))}
              {navTrailingItem && <li>{navTrailingItem}</li>}
            </ul>

            <button
              type="button"
              aria-label={translate('siteNav.openMenu')}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground transition hover:bg-gray-100 lg:hidden"
            >
              <MenuIcon className="h-6 w-6" />
            </button>
          </div>
        </nav>
      </div>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white lg:hidden">
          <div className="flex h-16 items-center justify-between border-b border-border px-6">
            <NextLink href={logoHref} onClick={closeMobileMenu} className="flex min-w-0 items-center gap-1">
              {logoContent}
            </NextLink>
            <button
              type="button"
              aria-label={translate('siteNav.closeMenu')}
              onClick={closeMobileMenu}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground transition hover:bg-gray-100"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          <ul className="flex flex-1 flex-col items-center justify-center gap-8 px-6">
            {NAV_ITEMS.map((item) => (
              <li key={item.hash}>
                <a
                  href={`${WOYTI_CONSUMER_URL}/#${item.hash}`}
                  onClick={closeMobileMenu}
                  className="font-display-bold text-3xl tracking-tight text-dark transition-colors hover:text-primary"
                >
                  {translate(item.labelKey)}
                </a>
              </li>
            ))}
            {navTrailingItem && <li className="mt-4">{navTrailingItem}</li>}
            <li className="mt-4">
              <LanguageSwitcher onSelect={closeMobileMenu} />
            </li>
          </ul>
        </div>
      )}
    </>
  );
};
